(() => {
    // Find cart items container - will be re-queried after refresh
    let cartItemsWrapper = document.querySelector('.cart-items__wrapper');
    
    // Helper function to get or re-query the wrapper
    const getCartItemsWrapper = () => {
      const wrapper = document.querySelector('.cart-items__wrapper');
      if (wrapper) {
        cartItemsWrapper = wrapper;
      }
      return cartItemsWrapper;
    };
  
    // Audio playback state
    let activeRow = null;
    let activePlayer = null;
  
    // Format time helper
    const fmt = (sec) => {
      if (!isFinite(sec) || sec < 0) return '0:00';
      const m = Math.floor(sec / 60);
      const s = Math.floor(sec % 60).toString().padStart(2, '0');
      return `${m}:${s}`;
    };
  
    // Normalize duration text - handles both human-readable (mm:ss) and raw seconds
    const normalizeDurationText = (val) => {
      if (!val || val === '0:00') return '0:00';
      if (typeof val === 'number') return fmt(val);
      if (/^\d+$/.test(String(val).trim())) return fmt(parseInt(val, 10));
      return String(val).trim();
    };
  
    // Initialize all time displays to show duration from metafield on page load
    // Preserves current time for playing/paused audio to prevent refresh issues
    const initializeTimeDisplays = () => {
      const wrapper = getCartItemsWrapper();
      if (!wrapper) return;
      const allRows = wrapper.querySelectorAll('.cart-item-card');
      allRows.forEach(row => {
        const currentTimeSpan = row.querySelector('.cart-item-card__current-time');
        if (!currentTimeSpan) return;
        
        // Check if this row has active audio
        const audioElement = row.querySelector('audio');
        const isPlaying = row.classList.contains('playing');
        const isPaused = currentTimeSpan.dataset.isPaused === 'true';
        const hasCurrentTime = currentTimeSpan.dataset.currentTime;
        const hasActiveAudio = audioElement && (isPlaying || (!audioElement.paused && audioElement.currentTime > 0) || (isPaused && hasCurrentTime));
        
        // If audio is active, preserve the current time display
        if (hasActiveAudio && audioElement && audioElement.duration && isFinite(audioElement.duration)) {
          if (isPlaying || !audioElement.paused) {
            // Audio is playing - show remaining time
            const remaining = Math.max(0, audioElement.duration - audioElement.currentTime);
            currentTimeSpan.textContent = fmt(remaining);
          } else if (isPaused && hasCurrentTime) {
            // Audio is paused - restore from stored time
            const currentTime = parseFloat(hasCurrentTime);
            const remaining = Math.max(0, audioElement.duration - currentTime);
            currentTimeSpan.textContent = fmt(remaining);
            // Restore audio currentTime if it was lost
            if (audioElement.currentTime !== currentTime) {
              audioElement.currentTime = currentTime;
            }
          } else {
            // Audio exists but state unclear - use currentTime if available
            const remaining = Math.max(0, audioElement.duration - (audioElement.currentTime || 0));
            currentTimeSpan.textContent = fmt(remaining);
          }
          return; // Don't reset, preserve current state
        }
        
        // Only reset time display if row is not playing and has no active audio
        if (!isPlaying && (!audioElement || audioElement.paused)) {
          const initialDuration = currentTimeSpan.dataset.initialDuration || currentTimeSpan.textContent.trim();
          if (initialDuration && initialDuration !== '0:00') {
            currentTimeSpan.textContent = normalizeDurationText(initialDuration);
          }
        }
      });
    };
  
    // Toggle play/pause icons
    const setPlayIcon = (row, playing, loading = false) => {
      const playIconContainer = row.querySelector('.cart-item-card__playicon');
      if (!playIconContainer) return;
      
      const playIcon = playIconContainer.querySelector('.icon-play');
      const pauseIcon = playIconContainer.querySelector('.icon-pause');
      if (!playIcon || !pauseIcon) return;
      
      if (loading) {
        playIcon.style.display = 'none';
        pauseIcon.style.display = 'none';
        row.classList.remove('playing');
      } else {
        playIcon.style.display = playing ? 'none' : 'block';
        pauseIcon.style.display = playing ? 'block' : 'none';
        if (playing) {
          row.classList.add('playing');
        } else {
          row.classList.remove('playing');
        }
      }
    };
  
    // Reset all play icons except the active one
    const resetAllPlayIcons = (exceptRow = null) => {
      const wrapper = getCartItemsWrapper();
      if (!wrapper) return;
      wrapper.querySelectorAll('.cart-item-card').forEach(row => {
        if (row !== exceptRow) {
          setPlayIcon(row, false);
          row.classList.remove('playing');
          const currentTimeSpan = row.querySelector('.cart-item-card__current-time');
          if (currentTimeSpan) {
            const audioElement = row.querySelector('audio');
            if (audioElement && isFinite(audioElement.duration)) {
              currentTimeSpan.textContent = fmt(audioElement.duration);
            } else {
              currentTimeSpan.textContent = normalizeDurationText(currentTimeSpan.dataset.initialDuration || '0:00');
            }
          }
        }
      });
    };
  
    // Initialize Osaria Player for a cart item
    const initOsariaPlayer = (row) => {
      const osariaData = row.dataset.productOsariaAudioData;
      if (!osariaData || osariaData === 'null' || osariaData === '') {
        return null;
      }
      return row.querySelector('.osaria-player-custom-product-view');
    };
  
    // Track which items are already set up
    const setupItems = new Set();
    
    // Helper to update time display and seek bar
    const updateTimeAndSeek = (row, audioElement) => {
      if (activeRow !== row) return;
      
      const currentTimeSpan = row.querySelector('.cart-item-card__current-time');
      const seek = row.querySelector('.cart-item-card__seek');
      
      if (currentTimeSpan && audioElement.duration && isFinite(audioElement.duration)) {
        const remaining = Math.max(0, audioElement.duration - audioElement.currentTime);
        currentTimeSpan.textContent = fmt(remaining);
      }
      
      if (seek && audioElement.duration && isFinite(audioElement.duration)) {
        const progress = (audioElement.currentTime / audioElement.duration) * 1000;
        seek.value = Math.round(progress);
        const fillWidth = (audioElement.currentTime / audioElement.duration) * 100;
        seek.style.setProperty('--value', `${fillWidth}%`);
        const seekFill = row.querySelector('.cart-item-card__seek-fill');
        if (seekFill) seekFill.style.width = `${fillWidth}%`;
      }
    };
  
    // Setup audio element event listeners
    const setupAudioEvents = (audioElement, row, player) => {
      if (!audioElement || audioElement.dataset.eventsSetup === 'true') return;
      audioElement.dataset.eventsSetup = 'true';
      
      audioElement.addEventListener('loadedmetadata', () => {
        const currentTimeSpan = row.querySelector('.cart-item-card__current-time');
        if (currentTimeSpan && isFinite(audioElement.duration)) {
          if (activeRow !== row || audioElement.paused) {
            const formattedDuration = fmt(audioElement.duration);
            currentTimeSpan.textContent = formattedDuration;
            currentTimeSpan.dataset.totalDuration = audioElement.duration;
          }
        }
      });
  
      audioElement.addEventListener('durationchange', () => {
        const currentTimeSpan = row.querySelector('.cart-item-card__current-time');
        if (currentTimeSpan && isFinite(audioElement.duration)) {
          if (activeRow !== row || audioElement.paused) {
            const formattedDuration = fmt(audioElement.duration);
            currentTimeSpan.textContent = formattedDuration;
            currentTimeSpan.dataset.totalDuration = audioElement.duration;
          }
        }
      });
  
      audioElement.addEventListener('canplay', () => {
        if (activeRow !== row || audioElement.paused) {
          const currentTimeSpan = row.querySelector('.cart-item-card__current-time');
          if (currentTimeSpan && isFinite(audioElement.duration)) {
            const formattedDuration = fmt(audioElement.duration);
            currentTimeSpan.textContent = formattedDuration;
            currentTimeSpan.dataset.totalDuration = audioElement.duration;
          }
        }
        setPlayIcon(row, true);
      });
  
      audioElement.addEventListener('play', () => {
        const wrapper = getCartItemsWrapper();
        if (wrapper) {
          wrapper.querySelectorAll('.cart-item-card').forEach(r => {
            r.classList.remove('playing');
            // Clear pause state when switching tracks
            const timeSpan = r.querySelector('.cart-item-card__current-time');
            if (timeSpan) {
              timeSpan.removeAttribute('data-is-paused');
              timeSpan.removeAttribute('data-current-time');
            }
          });
        }
        row.classList.add('playing');
        resetAllPlayIcons(row);
        setPlayIcon(row, true);
        activeRow = row;
        
        const currentTimeSpan = row.querySelector('.cart-item-card__current-time');
        if (currentTimeSpan) {
          // Clear pause state when playing
          currentTimeSpan.removeAttribute('data-is-paused');
          if (audioElement.duration && isFinite(audioElement.duration)) {
            // Show remaining time, not full duration
            const remaining = Math.max(0, audioElement.duration - (audioElement.currentTime || 0));
            currentTimeSpan.textContent = fmt(remaining);
          }
        }
        
        const titleElement = row.querySelector('.cart-item-card__title');
        const titleText = titleElement?.textContent?.trim() || 'Unknown Track';
        const albumArtElement = row.querySelector('.cart-item-card__cover-image');
        const albumArtSrc = albumArtElement?.src || '';
        
        const trackData = {
          title: titleText,
          artist: 'Unknown Artist',
          albumArt: albumArtSrc,
          duration: audioElement.duration || 0,
          element: row
        };
        
        if (window.StickyAudioPlayer) {
          window.StickyAudioPlayer.show(trackData);
          window.StickyAudioPlayer.connect(audioElement);
          window.StickyAudioPlayer.updatePlayPause(true);
        }
      });
  
      audioElement.addEventListener('pause', () => {
        row.classList.remove('playing');
        setPlayIcon(row, false);
        
        const currentTimeSpan = row.querySelector('.cart-item-card__current-time');
        if (currentTimeSpan && audioElement.duration && isFinite(audioElement.duration) && audioElement.currentTime !== undefined) {
          const remaining = Math.max(0, audioElement.duration - audioElement.currentTime);
          currentTimeSpan.textContent = fmt(remaining);
          // Store the current time in dataset to preserve it during refreshes
          currentTimeSpan.dataset.currentTime = audioElement.currentTime.toString();
          currentTimeSpan.dataset.isPaused = 'true';
        }
        
        if (window.StickyAudioPlayer) {
          window.StickyAudioPlayer.updatePlayPause(false);
        }
      });
  
      audioElement.addEventListener('timeupdate', () => {
        updateTimeAndSeek(row, audioElement);
      });
  
      audioElement.addEventListener('ended', () => {
        row.classList.remove('playing');
        setPlayIcon(row, false);
        const currentAudio = row.querySelector('audio') || audioElement;
        const currentTimeSpan = row.querySelector('.cart-item-card__current-time');
        if (currentTimeSpan && currentAudio && isFinite(currentAudio.duration)) {
          currentTimeSpan.textContent = fmt(currentAudio.duration);
        } else if (currentTimeSpan) {
          currentTimeSpan.textContent = normalizeDurationText(currentTimeSpan.dataset.initialDuration || '0:00');
        }
        activeRow = null;
        activePlayer = null;
      });
  
      audioElement.addEventListener('error', () => {
        row.classList.remove('playing');
        setPlayIcon(row, false);
      });
    };
  
    // Direct refresh method
    window.refreshCartItemsAudio = () => {
      setupItems.clear();
      const allButtons = document.querySelectorAll('.cart-item-card__playbtn[data-cart-audio-setup="true"]');
      allButtons.forEach(btn => {
        btn.removeAttribute('data-cart-audio-setup');
      });
      
      cartItemsWrapper = document.querySelector('.cart-items__wrapper');
      
      if (!cartItemsWrapper) {
        setTimeout(() => {
          cartItemsWrapper = document.querySelector('.cart-items__wrapper');
          if (cartItemsWrapper) {
            setupCartItemAudio();
          }
        }, 200);
        return;
      }
      
      initializeTimeDisplays();
      setupCartItemAudio();
      reinitializeOsariaPlayers();
    };
  
    // Set up play buttons for cart items
    const setupCartItemAudio = () => {
      const wrapper = getCartItemsWrapper();
      if (!wrapper) return;
      
      const cartRows = wrapper.querySelectorAll('.cart-item-card[data-product-osaria-audio-data]');
      if (cartRows.length === 0) return;
  
      // Clear setupItems Set when HTML is replaced
      const existingSetupItems = Array.from(setupItems).filter(item => document.contains(item));
      if (existingSetupItems.length < setupItems.size) {
        setupItems.clear();
      }
  
      cartRows.forEach((row) => {
        if (setupItems.has(row)) return;
  
        const btn = row.querySelector('.cart-item-card__playbtn');
        if (!btn) return;
        
        if (btn.dataset.cartAudioSetup === 'true') return;
        
        btn.dataset.cartAudioSetup = 'true';
        setupItems.add(row);
  
        const clickHandler = (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          
          const osariaData = row.dataset.productOsariaAudioData;
          
          if (!osariaData || osariaData === 'null' || osariaData === '') {
            alert('No audio source found for this track. Please check Osaria metafields.');
            return;
          }
  
          const existingAudio = row.querySelector('audio');
          
          // If same track is already playing, pause it
          if (row === activeRow && activePlayer && existingAudio && !existingAudio.paused) {
            row.classList.remove('playing');
            existingAudio.pause();
            setPlayIcon(row, false);
            
            if (window.StickyAudioPlayer) {
              window.StickyAudioPlayer.updatePlayPause(false);
            }
            return;
          }
  
          // If audio element exists and is paused, resume it
          if (existingAudio && existingAudio.paused) {
            if (activeRow && activeRow !== row) {
              const prevAudio = activeRow.querySelector('audio');
              if (prevAudio) {
                prevAudio.pause();
              }
              setPlayIcon(activeRow, false);
              activeRow.classList.remove('playing');
              const prevCurrentTime = activeRow.querySelector('.cart-item-card__current-time');
              if (prevCurrentTime) {
                const prevAudio = activeRow.querySelector('audio');
                if (prevAudio && isFinite(prevAudio.duration)) {
                  prevCurrentTime.textContent = fmt(prevAudio.duration);
                } else {
                  prevCurrentTime.textContent = normalizeDurationText(prevCurrentTime.dataset.initialDuration || '0:00');
                }
              }
            }
            
            resetAllPlayIcons(row);
            setPlayIcon(row, false);
            
            activeRow = row;
            activePlayer = row.querySelector('.osaria-player-custom-product-view');
            
            // Ensure timeupdate listener is set up
            if (existingAudio && existingAudio.dataset.resumeTimeupdateSetup !== 'true') {
              existingAudio.dataset.resumeTimeupdateSetup = 'true';
              existingAudio.addEventListener('timeupdate', () => {
                updateTimeAndSeek(row, existingAudio);
              });
            }
            
            const playPromise = existingAudio.play();
            if (playPromise !== undefined) {
              playPromise.then(() => {
                row.classList.add('playing');
                setPlayIcon(row, true);
                
                const currentTimeSpan = row.querySelector('.cart-item-card__current-time');
                if (currentTimeSpan && existingAudio.duration && isFinite(existingAudio.duration)) {
                  const currentText = currentTimeSpan.textContent.trim();
                  if (currentText === '0:00' || currentText === fmt(existingAudio.duration)) {
                    currentTimeSpan.textContent = fmt(existingAudio.duration);
                  }
                }
                
                const titleElement = row.querySelector('.cart-item-card__title');
                const titleText = titleElement?.textContent?.trim() || 'Unknown Track';
                const albumArtElement = row.querySelector('.cart-item-card__cover-image');
                const albumArtSrc = albumArtElement?.src || '';
                
                const trackData = {
                  title: titleText,
                  artist: 'Unknown Artist',
                  albumArt: albumArtSrc,
                  duration: existingAudio.duration || 0,
                  element: row
                };
                
                if (window.StickyAudioPlayer) {
                  window.StickyAudioPlayer.show(trackData);
                  window.StickyAudioPlayer.connect(existingAudio);
                  window.StickyAudioPlayer.updatePlayPause(true);
                }
              }).catch(() => {
                row.classList.remove('playing');
                setPlayIcon(row, false);
              });
            }
            
            return;
          }
  
          // Stop any currently playing track
          if (activeRow && activeRow !== row) {
            const prevAudio = activeRow.querySelector('audio');
            if (prevAudio) {
              prevAudio.pause();
            }
            setPlayIcon(activeRow, false);
            activeRow.classList.remove('playing');
          }
  
          resetAllPlayIcons();
          activeRow = row;
          setPlayIcon(row, false, true);
  
          const player = initOsariaPlayer(row);
          if (player) {
            activePlayer = player;
  
            let fallbackAudioUrl = null;
            try {
              const osariaDataJson = JSON.parse(osariaData);
              if (osariaDataJson.audio_url) {
                fallbackAudioUrl = osariaDataJson.audio_url;
              } else if (osariaDataJson.url) {
                fallbackAudioUrl = osariaDataJson.url;
              } else if (osariaDataJson.preview_url) {
                fallbackAudioUrl = osariaDataJson.preview_url;
              } else if (osariaDataJson.files && osariaDataJson.files.length > 0) {
                const domain = window.Shopify?.shop || window.location.hostname;
                const productId = row.dataset.productId;
                const filename = osariaDataJson.files[0].filename;
                if (domain && productId && filename) {
                  fallbackAudioUrl = `https://osaria-system.eu/_audio_bucket_player/${domain}/${productId}/${filename}`;
                }
              }
            } catch (e) {
              if (osariaData && osariaData.includes('http')) {
                fallbackAudioUrl = osariaData;
              }
            }
  
            // Make element temporarily visible for Osaria initialization
            const originalDisplay = player.style.display;
            const originalVisibility = player.style.visibility;
            const originalPosition = player.style.position;
            const originalLeft = player.style.left;
            const originalTop = player.style.top;
            
            player.style.display = 'block';
            player.style.visibility = 'visible';
            player.style.position = 'static';
            player.style.left = 'auto';
            player.style.top = 'auto';
            
            player.dispatchEvent(new Event('DOMNodeInserted', { bubbles: true }));
            player.dispatchEvent(new CustomEvent('osaria:init', { 
              bubbles: true,
              detail: { element: player, productId: row.dataset.productId } 
            }));
            
            if (window.OsariaPlayer && typeof window.OsariaPlayer.init === 'function') {
              window.OsariaPlayer.init(player);
            }
            
            setTimeout(() => {
              player.style.display = originalDisplay || '';
              player.style.visibility = originalVisibility || '';
              player.style.position = originalPosition || '';
              player.style.left = originalLeft || '';
              player.style.top = originalTop || '';
            }, 500);
  
            let checkCount = 0;
            const maxChecks = 50;
            
            const checkForAudio = () => {
              checkCount++;
              let audioElement = row.querySelector('audio');
              
              if (!audioElement && fallbackAudioUrl && checkCount > 10) {
                audioElement = document.createElement('audio');
                audioElement.src = fallbackAudioUrl;
                audioElement.preload = 'auto';
                audioElement.style.display = 'none';
                audioElement.setAttribute('controls', 'false');
                player.appendChild(audioElement);
              }
              
              if (audioElement) {
                if (audioElement.dataset.eventsSetup === 'true') {
                  activeRow = row;
                  activePlayer = player;
                  resetAllPlayIcons(row);
                  setPlayIcon(row, false);
                  
                  audioElement.play().then(() => {
                    row.classList.add('playing');
                    setPlayIcon(row, true);
                    
                    const titleElement = row.querySelector('.cart-item-card__title');
                    const titleText = titleElement?.textContent?.trim() || 'Unknown Track';
                    const albumArtElement = row.querySelector('.cart-item-card__cover-image');
                    const albumArtSrc = albumArtElement?.src || '';
                    
                    const trackData = {
                      title: titleText,
                      artist: 'Unknown Artist',
                      albumArt: albumArtSrc,
                      duration: audioElement.duration || 0,
                      element: row
                    };
                    
                    if (window.StickyAudioPlayer) {
                      window.StickyAudioPlayer.show(trackData);
                      window.StickyAudioPlayer.connect(audioElement);
                      window.StickyAudioPlayer.updatePlayPause(true);
                    }
                  }).catch(() => {
                    row.classList.remove('playing');
                    setPlayIcon(row, false);
                  });
                  return;
                }
                
                setupAudioEvents(audioElement, row, player);
                
                // Set up MutationObserver for new audio elements
                const playerObserver = new MutationObserver((mutations) => {
                  mutations.forEach((mutation) => {
                    mutation.addedNodes.forEach((node) => {
                      if (node.tagName === 'AUDIO' && node.parentElement === player) {
                        setupAudioEvents(node, row, player);
                        node.addEventListener('loadedmetadata', () => {
                          if (activeRow === row && (node.paused || !node.currentTime)) {
                            const currentTimeSpan = row.querySelector('.cart-item-card__current-time');
                            if (currentTimeSpan && node.duration && isFinite(node.duration)) {
                              currentTimeSpan.textContent = fmt(node.duration);
                            }
                          }
                        });
                        node.addEventListener('play', () => {
                          activeRow = row;
                        });
                      }
                    });
                  });
                });
                
                playerObserver.observe(player, { childList: true, subtree: true });
                
                // Set up listeners for all existing audio elements
                const setupAllAudioListeners = () => {
                  const allAudios = row.querySelectorAll('audio');
                  allAudios.forEach((audio) => {
                    if (audio.dataset.cartTimeupdateSetup === 'true') return;
                    audio.dataset.cartTimeupdateSetup = 'true';
                    
                    audio.addEventListener('timeupdate', () => {
                      updateTimeAndSeek(row, audio);
                    });
                    
                    audio.addEventListener('loadedmetadata', () => {
                      if (activeRow === row && (audio.paused || !audio.currentTime)) {
                        const currentTimeSpan = row.querySelector('.cart-item-card__current-time');
                        if (currentTimeSpan && audio.duration && isFinite(audio.duration)) {
                          currentTimeSpan.textContent = fmt(audio.duration);
                        }
                      }
                    });
                    
                    audio.addEventListener('play', () => {
                      activeRow = row;
                    });
                  });
                };
                
                setupAllAudioListeners();
                setTimeout(setupAllAudioListeners, 100);
                setTimeout(setupAllAudioListeners, 500);
                
                audioElement.addEventListener('ended', () => {
                  playerObserver.disconnect();
                });
  
                const playPromise = audioElement.play();
                if (playPromise) {
                  playPromise.then(() => {
                    // Playback started
                  }).catch(() => {
                    row.classList.remove('playing');
                    setPlayIcon(row, false);
                  });
                }
              } else {
                if (checkCount < maxChecks) {
                  setTimeout(checkForAudio, 100);
                } else {
                  if (fallbackAudioUrl && checkCount === maxChecks) {
                    const audioElement = document.createElement('audio');
                    audioElement.src = fallbackAudioUrl;
                    audioElement.preload = 'auto';
                    audioElement.style.display = 'none';
                    audioElement.setAttribute('controls', 'false');
                    player.appendChild(audioElement);
                    audioElement.dataset.eventsSetup = 'true';
                    setupAudioEvents(audioElement, row, player);
                    audioElement.load();
                    return;
                  }
                  
                  row.classList.remove('playing');
                  setPlayIcon(row, false);
                  alert('Audio player failed to initialize. Please try again.');
                }
              }
            };
  
            checkForAudio();
          } else {
            row.classList.remove('playing');
            setPlayIcon(row, false);
          }
        };
        
        btn.addEventListener('click', clickHandler, { capture: true, once: false });
        btn.addEventListener('click', clickHandler, { capture: false, once: false });
      });
    };
  
    // Function to re-initialize Osaria players for cart items
    const reinitializeOsariaPlayers = () => {
      const wrapper = getCartItemsWrapper();
      if (!wrapper) return;
      const playerDivs = wrapper.querySelectorAll('.osaria-player-custom-product-view');
      
      playerDivs.forEach((playerDiv) => {
        const originalDisplay = playerDiv.style.display;
        const originalVisibility = playerDiv.style.visibility;
        const originalPosition = playerDiv.style.position;
        const originalLeft = playerDiv.style.left;
        const originalTop = playerDiv.style.top;
        
        playerDiv.style.display = 'block';
        playerDiv.style.visibility = 'visible';
        playerDiv.style.position = 'static';
        playerDiv.style.left = 'auto';
        playerDiv.style.top = 'auto';
        
        playerDiv.dispatchEvent(new Event('DOMNodeInserted', { bubbles: true }));
        playerDiv.dispatchEvent(new CustomEvent('osaria:init', { 
          bubbles: true,
          detail: { element: playerDiv, productId: playerDiv.dataset.productId } 
        }));
        
        // Wait for OsariaPlayer to be available, then initialize
        let retryCount = 0;
        const maxRetries = 50; // Max 5 seconds (50 * 100ms)
        const initOsaria = () => {
          if (window.OsariaPlayer && typeof window.OsariaPlayer.init === 'function') {
            window.OsariaPlayer.init(playerDiv);
          } else if (retryCount < maxRetries) {
            // Retry after a short delay if OsariaPlayer is not yet loaded
            retryCount++;
            setTimeout(initOsaria, 100);
          }
        };
        initOsaria();
        
        setTimeout(() => {
          playerDiv.style.display = originalDisplay || '';
          playerDiv.style.visibility = originalVisibility || '';
          playerDiv.style.position = originalPosition || '';
          playerDiv.style.left = originalLeft || '';
          playerDiv.style.top = originalTop || '';
        }, 200);
      });
    };
  
    // Initialize when DOM is ready
    const initialize = () => {
      initializeTimeDisplays();
      setupCartItemAudio();
      setTimeout(reinitializeOsariaPlayers, 500);
    };
  
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initialize);
    } else {
      initialize();
    }
  
    // Re-initialize when cart updates
    const observer = new MutationObserver(() => {
      initializeTimeDisplays();
      setupCartItemAudio();
      setTimeout(reinitializeOsariaPlayers, 300);
    });
  
    if (cartItemsWrapper) {
      observer.observe(cartItemsWrapper, {
        childList: true,
        subtree: true
      });
    }
  
    // Listen for cart drawer open events
    document.addEventListener('cart:drawer:open', () => {
      cartItemsWrapper = document.querySelector('.cart-items__wrapper');
      
      setupItems.clear();
      const allButtons = document.querySelectorAll('.cart-item-card__playbtn[data-cart-audio-setup="true"]');
      allButtons.forEach(btn => {
        btn.removeAttribute('data-cart-audio-setup');
      });
      
      setTimeout(() => {
        initializeTimeDisplays();
        setupCartItemAudio();
        reinitializeOsariaPlayers();
      }, 200);
    });
  
    // Listen for cart content refresh events
    // Use debouncing to prevent excessive refreshes during play/pause
    let cartRefreshTimeout = null;
    document.addEventListener('cart:content:refreshed', () => {
      // Clear any pending refresh
      if (cartRefreshTimeout) {
        clearTimeout(cartRefreshTimeout);
      }
      
      // Debounce the refresh to prevent it from running during active playback
      cartRefreshTimeout = setTimeout(() => {
        // Check if any audio is currently playing - if so, preserve state
        const wrapper = getCartItemsWrapper();
        const hasPlayingAudio = wrapper && wrapper.querySelector('.cart-item-card.playing audio:not([paused])');
        
        setupItems.clear();
        const allButtons = document.querySelectorAll('.cart-item-card__playbtn[data-cart-audio-setup="true"]');
        allButtons.forEach(btn => {
          btn.removeAttribute('data-cart-audio-setup');
        });
        
        cartItemsWrapper = document.querySelector('.cart-items__wrapper');
        
        if (!cartItemsWrapper) {
          setTimeout(() => {
            cartItemsWrapper = document.querySelector('.cart-items__wrapper');
            if (cartItemsWrapper) {
              initializeTimeDisplays(); // This now preserves active audio time
              setupCartItemAudio();
              // Only reinitialize Osaria players if not currently playing
              if (!hasPlayingAudio) {
                reinitializeOsariaPlayers();
              }
            }
          }, 200);
          return;
        }
        
        setTimeout(() => {
          initializeTimeDisplays(); // This now preserves active audio time
          setupCartItemAudio();
          // Only reinitialize Osaria players if not currently playing
          if (!hasPlayingAudio) {
            reinitializeOsariaPlayers();
          }
        }, 300);
      }, 100); // Debounce to 100ms
    });
    
    // Setup scrollbar for chips container
    const setupChipsScrollbar = () => {
      const chipsContainers = document.querySelectorAll('.cart-items__chips');
      
      chipsContainers.forEach((container) => {
        let scrollTimeout;
        
        const handleScroll = () => {
          container.classList.add('scrolling');
          clearTimeout(scrollTimeout);
          scrollTimeout = setTimeout(() => {
            container.classList.remove('scrolling');
          }, 500);
        };
        
        container.addEventListener('scroll', handleScroll, { passive: true });
      });
    };
  
    // Initialize chips scrollbar detection
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', setupChipsScrollbar);
    } else {
      setupChipsScrollbar();
    }
  
    // Re-initialize when cart updates - setup scrollbar for new chips
    const setupChipsObserver = () => {
      const wrapper = getCartItemsWrapper();
      if (!wrapper) return;
      
      const setupNewChips = () => {
        const newChips = wrapper.querySelectorAll('.cart-items__chips:not([data-scroll-setup])');
        newChips.forEach((chips) => {
          chips.setAttribute('data-scroll-setup', 'true');
          let scrollTimeout;
          
          const handleScroll = () => {
            chips.classList.add('scrolling');
            clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(() => {
              chips.classList.remove('scrolling');
            }, 500);
          };
          
          chips.addEventListener('scroll', handleScroll, { passive: true });
        });
      };
      
      setupNewChips();
      
      const chipsObserver = new MutationObserver(() => {
        setupNewChips();
      });
      
      chipsObserver.observe(wrapper, {
        childList: true,
        subtree: true
      });
    };
    
    setupChipsObserver();
    
    document.addEventListener('cart:content:refreshed', () => {
      setupChipsObserver();
    });
  })();